import React from 'react';
import { Zap, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Shown inside the chat panel when the user has hit their daily limit (429).
 *
 * Props:
 *   usage    – { message_count, daily_limit, plan, resets_at } from useUsageQuota
 *   variant  – 'banner' (inline strip) | 'banner-block' (replaces input area) | 'overlay' (full-panel block, default)
 */
export default function LimitReachedBanner({ usage, variant = 'overlay' }) {
  const navigate = useNavigate();

  const resetsAt = usage?.resets_at ? new Date(usage.resets_at) : null;
  const resetsAtStr = resetsAt
    ? resetsAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })
    : 'midnight UTC';

  if (variant === 'banner-block') {
    return (
      <div className="p-4 flex flex-col items-center gap-3 text-center">
        <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
          <Zap size={15} className="text-amber-500 fill-amber-400 flex-shrink-0" />
          <span>
            You've used{' '}
            <span className="font-bold">{usage?.message_count ?? '—'} / {usage?.daily_limit ?? '—'}</span>{' '}
            messages today.
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <Clock size={12} />
            Resets at {resetsAtStr}
          </span>
          <button
            onClick={() => navigate('/upgrade')}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            <Zap size={12} className="fill-yellow-300 text-yellow-300" />
            Upgrade for more
          </button>
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="mx-4 mb-2 flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm">
        <div className="flex items-center gap-2 text-amber-700 font-medium">
          <Clock size={14} />
          <span>
            {usage?.message_count ?? '—'} / {usage?.daily_limit ?? '—'} messages today — resets at {resetsAtStr}
          </span>
        </div>
        <button
          onClick={() => navigate('/upgrade')}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
        >
          <Zap size={12} className="fill-yellow-300 text-yellow-300" />
          Upgrade
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6 py-8 gap-5">
      <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
        <Zap size={28} className="text-amber-500 fill-amber-400" />
      </div>

      <div>
        <h3 className="text-base font-bold text-gray-900 mb-1">Daily limit reached</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          You've used{' '}
          <span className="font-semibold text-gray-700">
            {usage?.message_count ?? '—'} / {usage?.daily_limit ?? '—'}
          </span>{' '}
          messages on the{' '}
          <span className="font-semibold text-gray-700">{usage?.plan ?? 'Free'}</span> plan today.
        </p>
      </div>

      <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-500">
        <Clock size={13} className="text-gray-400" />
        Resets at <span className="font-medium text-gray-700 ml-1">{resetsAtStr}</span>
      </div>

      <button
        onClick={() => navigate('/upgrade')}
        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-md"
      >
        <Zap size={15} className="fill-yellow-300 text-yellow-300" />
        Upgrade for more messages
        <ArrowRight size={15} />
      </button>

      <p className="text-[11px] text-gray-400">
        Pro plan: 200 messages/day
      </p>
    </div>
  );
}
